import React, { useState, useEffect } from 'react';
import {
    Form,
    Button,
    Container,
    Row,
    Col,
    Card,
    Modal,
    Toast,
    ToastContainer,
    Spinner
} from 'react-bootstrap';
import axios from 'axios';

const BannerUpload = () => {
    const [title, setTitle] = useState('');
    const [desc, setDesc] = useState('');
    const [image, setImage] = useState(null);
    const [banners, setBanners] = useState([]);
    const [editingBanner, setEditingBanner] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [loadingBanners, setLoadingBanners] = useState(false); // State to handle loading indicator for banners
    const [categories, setCategories] = useState([]); // State to store fetched categories
    const [loadingCategories, setLoadingCategories] = useState(false); // State to handle category loading
    const [selectedCategoryId, setSelectedCategoryId] = useState(''); // State for selected category

    // Function to fetch categories
    const fetchCategories = async () => {
        setLoadingCategories(true);
        try {
            const response = await axios.get("https://drdprints.com/api/get-category");
            if (response.status === 200 && response.data.categories) {
                setCategories(response.data.categories);
                // Set the first category as default if no category is selected
                if (response.data.categories.length > 0) {
                    setSelectedCategoryId(response.data.categories[0].id.toString());
                }
            } else {
                setToastMessage('No categories found.');
                setShowToast(true);
            }
        } catch (error) {
            setToastMessage('Error fetching categories.');
            setShowToast(true);
            console.error('Error fetching categories:', error);
        } finally {
            setLoadingCategories(false);
        }
    };

    // Function to fetch banners associated with the selected category
    const fetchBanners = async (categoryId) => {
        if (!categoryId) return; // Exit if categoryId is not provided
        setLoadingBanners(true); // Start loading
        try {
            // Assuming the API supports filtering by categoryId via query parameter
            const response = await axios.get(`https://drdprints.com/api/get-banners?cat_id=${categoryId}`);

            // If the API does not support filtering, fetch all and filter client-side
            // const response = await axios.get('https://drdprints.com/api/get-banners');
            // const filteredBanners = response.data.banners.filter(banner => String(banner.cat_id) === String(categoryId));
            // setBanners(filteredBanners);

            setBanners(response.data.banners || []); // Update banners state
        } catch (error) {
            setToastMessage('Error fetching banners.');
            setShowToast(true);
            console.error('Error fetching banners:', error);
        } finally {
            setLoadingBanners(false); // End loading
        }
    };

    // Fetch categories on component mount
    useEffect(() => {
        fetchCategories();
    }, []); // Empty dependency array ensures this runs once on mount

    // Fetch banners whenever the selected category changes
    useEffect(() => {
        if (selectedCategoryId) { // Ensure categoryId is available
            fetchBanners(selectedCategoryId);
        }
    }, [selectedCategoryId]);

    // Handle category selection from dropdown
    const handleCategoryChange = (e) => {
        const selectedId = e.target.value;
        setSelectedCategoryId(selectedId);
    };

    // Handle form submission for uploading/updating banners
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!selectedCategoryId) {
            setToastMessage('Please select a category.');
            setShowToast(true);
            return;
        }

        if (!title.trim()) {
            setToastMessage('Please enter a title.');
            setShowToast(true);
            return;
        }

        if (!desc.trim()) {
            setToastMessage('Please enter a description.');
            setShowToast(true);
            return;
        }

        if (!image && !editingBanner) { // Require image only if not editing
            setToastMessage('Please select an image.');
            setShowToast(true);
            return;
        }

        const formData = new FormData();
        formData.append('cat_id', selectedCategoryId); // Append category ID
        formData.append('title', title);
        formData.append('desc', desc);
        if (image) {
            formData.append('image', image);
        }

        try {
            const response = editingBanner
                ? await axios.put(`https://drdprints.com/api/update-banner/${editingBanner.id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                : await axios.post('https://drdprints.com/api/store-banner', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

            setToastMessage(editingBanner ? 'Banner updated successfully.' : 'Banner uploaded successfully.');
            setShowToast(true);

            // Reset the form and close the modal
            setTitle('');
            setDesc('');
            setImage(null);
            setEditingBanner(null);
            setShowModal(false);

            // Refresh the banners list
            fetchBanners(selectedCategoryId);
        } catch (error) {
            // Extract error messages from the response if available
            const errorResponse = error.response?.data?.errors;
            if (errorResponse) {
                const messages = Object.values(errorResponse).flat().join(' ');
                setToastMessage(`Error: ${messages}`);
            } else {
                setToastMessage(editingBanner ? 'Error updating banner.' : 'Error uploading banner.');
            }
            setShowToast(true);
            console.error('Error uploading/updating banner:', error);
        }
    };

    // Handle editing a banner
    const handleEdit = (banner) => {
        setTitle(banner.title);
        setDesc(banner.desc);
        setEditingBanner(banner);
        setShowModal(true);
    };

    // Handle deleting a banner
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this banner?')) return; // Confirm deletion

        try {
            await axios.delete(`https://drdprints.com/api/deletebanner/${id}`);
            setToastMessage('Banner deleted successfully.');
            setShowToast(true);
            fetchBanners(selectedCategoryId); // Refresh the list after deletion
        } catch (error) {
            setToastMessage('Error deleting banner.');
            setShowToast(true);
            console.error('Error deleting banner:', error);
        }
    };

    // Handle closing the modal
    const handleCloseModal = () => {
        setShowModal(false);
        setEditingBanner(null);
        setTitle('');
        setDesc('');
        setImage(null);
    };

    // Handle image file selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Optional: Validate file type and size
            const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                setToastMessage('Please select a valid image file (JPEG, PNG, WEBP).');
                setShowToast(true);
                return;
            }

            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                setToastMessage('Image size should be less than 5MB.');
                setShowToast(true);
                return;
            }

            setImage(file);
        } else {
            setImage(null);
        }
    };

    return (
        <Container>
            <Row className="justify-content-md-center">
                <Col md={12}>
                    <h2 className="text-center my-4">Upload a New Banner</h2>
                    <Form onSubmit={handleSubmit}>
                        {/* Category Selection Dropdown */}
                        <Form.Group controlId="formCategory">
                            <Form.Label>Category</Form.Label>
                            {loadingCategories ? (
                                <Spinner animation="border" size="sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                            ) : (
                                <Form.Select
                                    value={selectedCategoryId}
                                    onChange={handleCategoryChange}
                                    required
                                >
                                    <option value="" disabled>Select a category</option>
                                    {categories.map((category) => (
                                        <option key={category.id} value={category.id}>
                                            {category.cat_title}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>

                        <Form.Group controlId="formTitle" className="mt-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter banner title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group controlId="formDesc" className="mt-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter banner description"
                                value={desc}
                                onChange={(e) => setDesc(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group controlId="formImage" className="mt-3">
                            <Form.Label>Image</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                {...(!editingBanner && { required: true })} // Require image only if not editing
                            />
                            {image && (
                                <img
                                    src={URL.createObjectURL(image)}
                                    alt="Selected Banner"
                                    style={{ width: '100%', marginTop: '10px', maxHeight: '300px', objectFit: 'cover' }}
                                />
                            )}
                        </Form.Group>

                        <Button variant="primary" type="submit" className="mt-4">
                            {editingBanner ? 'Update Banner' : 'Upload Banner'}
                        </Button>
                    </Form>

                    <h2 className="text-center my-4">All Banners</h2>

                    {loadingBanners ? (
                        <div className="text-center my-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    ) : banners.length === 0 ? (
                        <p className="text-center">No banners found for this category.</p>
                    ) : (
                        <Row>
                            {banners.map((banner) => (
                                <Col md={4} sm={6} xs={12} key={banner.id} className="mb-4">
                                    <Card>
                                        <Card.Img
                                            variant="top"
                                            src={banner.image
                                                ? `https://drdprints.com/uploads/banners/${banner.image}`
                                                : 'https://via.placeholder.com/150'}
                                            alt={banner.title}
                                            style={{ height: '200px', objectFit: 'cover' }}
                                        />
                                        <Card.Body>
                                            <Card.Title>{banner.title}</Card.Title>
                                            <Card.Text>{banner.desc}</Card.Text>
                                            <Button variant="warning" className="me-2" onClick={() => handleEdit(banner)}>Edit</Button>
                                            <Button variant="danger" onClick={() => handleDelete(banner.id)}>Delete</Button>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}
                </Col>
            </Row>

            {/* Modal for Editing/Uploading Banners */}
            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingBanner ? 'Edit Banner' : 'Upload a New Banner'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group controlId="formTitle">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter banner title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group controlId="formDesc" className="mt-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter banner description"
                                value={desc}
                                onChange={(e) => setDesc(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group controlId="formImage" className="mt-3">
                            <Form.Label>Image</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                {...(!editingBanner && { required: true })} // Require image only if not editing
                            />
                            {image && (
                                <img
                                    src={URL.createObjectURL(image)}
                                    alt="Selected Banner"
                                    style={{ width: '100%', marginTop: '10px', maxHeight: '300px', objectFit: 'cover' }}
                                />
                            )}
                        </Form.Group>

                        <Button variant="primary" type="submit" className="mt-4">
                            {editingBanner ? 'Update Banner' : 'Upload Banner'}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Toast Notifications for Feedback */}
            <ToastContainer position="top-end" className="p-3">
                <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide>
                    <Toast.Body>{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>
        </Container>
    )};

    export default BannerUpload;
